import * as React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatDateOnly, parseIsoDate, toIsoDate } from '@/lib/utils/custom-date/custom-date';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Button } from '@/components/ui-kit/button';
import { Calendar } from '@/components/ui-kit/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';

/** A from–to pair of days ("YYYY-MM-DD", both inclusive). */
export type DayRange = { from: string; to: string };

interface DateRangePickerProps {
  value: DayRange;
  onChange: (value: DayRange) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

const sameDay = (a: Date, b: Date) => a.getTime() === b.getTime();

/**
 * One trigger for a start/end pair (HR Module Prototype, Component Map › Date range picker): two months
 * on desktop and one on phones. The first click picks the start and the span follows the pointer until
 * the second click picks the end (either order) and closes it; closing after one click keeps that single
 * day. Values are plain days, never shifted to a time zone.
 */
export const DateRangePicker = ({
  value,
  onChange,
  min,
  max,
  disabled,
  className,
  id,
  'aria-label': ariaLabel,
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const isWide = useMediaQuery('(min-width: 768px)');
  const [open, setOpen] = React.useState(false);
  // The start picked by the first click of this opening, and the day under the pointer after it.
  const [start, setStart] = React.useState<Date | undefined>();
  const [hovered, setHovered] = React.useState<Date | undefined>();
  const from = parseIsoDate(value.from);
  const to = parseIsoDate(value.to);
  const [month, setMonth] = React.useState<Date>(from ?? new Date());
  const minDate = parseIsoDate(min);
  const maxDate = parseIsoDate(max);

  const handleOpenChange = (next: boolean) => {
    if (!next && start) onChange({ from: toIsoDate(start), to: toIsoDate(start) });
    if (next) setMonth(from ?? new Date());
    setStart(undefined);
    setHovered(undefined);
    setOpen(next);
  };

  const handleDayClick = (day: Date) => {
    if (!start) {
      setStart(day);
      return;
    }
    const [first, last] = day < start ? [day, start] : [start, day];
    onChange({ from: toIsoDate(first), to: toIsoDate(last) });
    setStart(undefined);
    setHovered(undefined);
    setOpen(false);
  };

  const preview = (() => {
    if (!start) return from ? { from, to: to ?? from } : undefined;
    const end = hovered ?? start;
    return end < start ? { from: end, to: start } : { from: start, to: end };
  })();

  const label =
    value.from && value.to
      ? value.from === value.to
        ? formatDateOnly(value.from)
        : `${formatDateOnly(value.from)} – ${formatDateOnly(value.to)}`
      : t('SELECT_DATE_RANGE');

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            // Sized like an input (32px, `input` border) and to its value (Component Map › Date range picker).
            'h-8 min-w-[168px] max-w-full justify-start gap-2 border-input px-2.5 font-normal tabular-nums',
            !value.from && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={isWide ? 2 : 1}
          month={month}
          onMonthChange={setMonth}
          selected={preview}
          onSelect={() => undefined}
          onDayClick={(day, modifiers) => !modifiers.disabled && handleDayClick(day)}
          onDayMouseEnter={(day) => start && !(hovered && sameDay(hovered, day)) && setHovered(day)}
          disabled={(date) => Boolean((minDate && date < minDate) || (maxDate && date > maxDate))}
          startMonth={minDate}
          endMonth={maxDate}
        />
      </PopoverContent>
    </Popover>
  );
};
